import { useCallback, useEffect, useState, useRef } from "react";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Custom hook for Google reCAPTCHA v3
 * Only loads the script when enabled is true
 * 
 * @param {boolean} enabled - Whether reCAPTCHA should be loaded/active
 * @returns {Object} - { executeRecaptcha, isReady, isLoading }
 */
export const useRecaptcha = (enabled = false) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const grecaptchaRef = useRef(null);

  useEffect(() => {
    // Don't load if not enabled or no site key
    if (!enabled || !RECAPTCHA_SITE_KEY) {
      return;
    }

    // Check if already loaded
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => {
        grecaptchaRef.current = window.grecaptcha;
        setIsReady(true);
      });
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(
      `script[src*="recaptcha/api.js"]`
    );
    if (existingScript) {
      // Wait for it to load
      existingScript.addEventListener("load", () => {
        window.grecaptcha?.ready(() => {
          grecaptchaRef.current = window.grecaptcha;
          setIsReady(true);
        });
      });
      return;
    }

    // Load the script
    setIsLoading(true);
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha?.ready(() => {
        grecaptchaRef.current = window.grecaptcha;
        setIsReady(true);
        setIsLoading(false);
      });
    };

    script.onerror = () => {
      console.error("Failed to load reCAPTCHA script");
      setIsLoading(false);
    };

    document.head.appendChild(script);

    // Cleanup function - don't remove script as it may be used elsewhere
    return () => {
      // Script cleanup is intentionally not done to avoid issues with
      // multiple components using reCAPTCHA
    };
  }, [enabled]);

  /**
   * Execute reCAPTCHA and get a token
   * @param {string} action - The action name for this verification (e.g., 'email_submit', 'phone_submit')
   * @returns {Promise<string|null>} - The reCAPTCHA token or null if failed/disabled
   */
  const executeRecaptcha = useCallback(
    async (action = "submit") => {
      // If not enabled, return null (form should proceed without token)
      if (!enabled) {
        return null;
      }

      // If no site key configured, return null
      if (!RECAPTCHA_SITE_KEY) {
        console.warn("reCAPTCHA site key not configured");
        return null;
      }

      // Wait for grecaptcha to be ready if not yet
      if (!grecaptchaRef.current && window.grecaptcha) {
        grecaptchaRef.current = window.grecaptcha;
      }

      if (!grecaptchaRef.current) {
        console.error("reCAPTCHA not ready");
        return null;
      }

      try {
        const token = await grecaptchaRef.current.execute(RECAPTCHA_SITE_KEY, {
          action,
        });
        return token;
      } catch (error) {
        console.error("reCAPTCHA execution failed:", error);
        return null;
      }
    },
    [enabled]
  );

  return {
    executeRecaptcha,
    isReady,
    isLoading,
    isEnabled: enabled && !!RECAPTCHA_SITE_KEY,
  };
};

export default useRecaptcha;
